import {
  FormEvent,
  FunctionComponent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next/pages";
import type { DraftProposal, ExtractedInfo } from "../../pages/api/chat";
import {
  CHAT_INTAKE_STORAGE_KEY,
  EMPTY_INFO,
  mergeInfo
} from "../../lib/chatIntake";
import { OPEN_CHAT_EVENT } from "../../lib/chatEvents";
import { trackEvent } from "../../lib/analytics";
import styles from "../../styles/home/HeroChat.module.css";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface ChipDefinition {
  label: string;
  prompt: string;
}

interface TimelineStep {
  period: string;
  title: string;
}

interface HeroChatProps {
  /** Fired when the chat appears/disappears so the hero can hide its copy */
  // eslint-disable-next-line no-unused-vars
  onChatModeChange?: (active: boolean) => void;
}

const HeroChat: FunctionComponent<HeroChatProps> = ({ onChatModeChange }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;
  // Hidden until the hero CTA opens it; chatMode starts with the first message
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [chatMode, setChatMode] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [info, setInfo] = useState<ExtractedInfo>(EMPTY_INFO);
  const [proposal, setProposal] = useState<DraftProposal | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const proposalRef = useRef<HTMLElement>(null);
  const proposalTrackedRef = useRef<boolean>(false);
  // Incremented on reset so in-flight replies of the old session are dropped
  const sessionRef = useRef<number>(0);

  // The CMS may serve a common namespace that predates these keys; i18next
  // then returns the key string instead of the array — render nothing rather
  // than crash the prerender.
  const chipsRaw = t("heroChat.chips", { returnObjects: true });
  const chips: ChipDefinition[] = Array.isArray(chipsRaw) ? chipsRaw : [];
  const timelineRaw = t("heroChat.proposal.timeline", { returnObjects: true });
  const timelineSteps: TimelineStep[] = Array.isArray(timelineRaw)
    ? timelineRaw
    : [];

  useEffect(() => {
    if (chatMode && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isLoading, chatMode]);

  useEffect(() => {
    if (proposal && !proposalTrackedRef.current) {
      proposalTrackedRef.current = true;
      trackEvent("hero-proposal-shown");
    }
  }, [proposal]);

  useEffect(() => {
    const openChat = () => {
      setIsOpen(true);
      // Focus once the reveal transition has mostly settled; focusing also
      // scrolls the composer into view
      window.setTimeout(() => inputRef.current?.focus(), 650);
    };
    window.addEventListener(OPEN_CHAT_EVENT, openChat);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, openChat);
  }, []);

  useEffect(() => {
    onChatModeChange?.(isOpen);
    if (isOpen) trackEvent("chat-open");
  }, [isOpen, onChatModeChange]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [isOpen]);

  const sendMessage = async (content: string) => {
    const trimmed = content.trim();
    if (!trimmed || isLoading) return;

    if (!chatMode) setChatMode(true);

    const session = sessionRef.current;
    const nextMessages: ChatMessage[] = [
      ...messages,
      { role: "user", content: trimmed }
    ];
    setMessages(nextMessages);
    setSuggestions([]);
    setInput("");
    setHasError(false);
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // The last 12 messages are enough context; keeps the payload small
          messages: nextMessages.slice(-12),
          locale
        })
      });

      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const data = await response.json();
      if (!data.reply) throw new Error("Empty reply");
      if (session !== sessionRef.current) return;

      setMessages([...nextMessages, { role: "assistant", content: data.reply }]);
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      setInfo((prev) => mergeInfo(prev, data.extracted));
      // Keep the last drafted sketch; a later extraction may return null
      if (data.proposal) setProposal(data.proposal);
    } catch {
      if (session === sessionRef.current) setHasError(true);
    } finally {
      if (session === sessionRef.current) setIsLoading(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage(input);
    }
  };

  const handleTakeover = () => {
    try {
      sessionStorage.setItem(CHAT_INTAKE_STORAGE_KEY, JSON.stringify(info));
    } catch {
      // Storage unavailable (e.g. blocked) — the form just starts empty
    }
    router.push("/workshop-vorbereitung");
  };

  const handleReset = () => {
    sessionRef.current += 1;
    proposalTrackedRef.current = false;
    setMessages([]);
    setSuggestions([]);
    setInput("");
    setInfo(EMPTY_INFO);
    setProposal(null);
    setHasError(false);
    setIsLoading(false);
    setChatMode(false);
    setIsOpen(false);
  };

  const containerClasses = [
    styles.experience,
    isOpen ? styles.open : "",
    chatMode ? styles.chatMode : "",
    proposal ? styles.hasProposal : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses} id="hero-chat">
      <section className={styles.heroChat} aria-label={t("heroChat.regionLabel")}>
        <button
          type="button"
          className={styles.close}
          onClick={() => setIsOpen(false)}
          aria-label={t("heroChat.close")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6 18 18" />
            <path d="M18 6 6 18" />
          </svg>
        </button>
        <div className={styles.chatGrid}>
          <div className={styles.conversation}>
            {!chatMode && (
              <div className={styles.promptHeadline}>
                <h2>{t("heroChat.headline")}</h2>
                <p>{t("heroChat.subline")}</p>
              </div>
            )}

            {chatMode && (
              <div
                className={styles.messages}
                ref={messagesRef}
                role="log"
                aria-live="polite"
              >
                <div className={`${styles.message} ${styles.assistant}`}>
                  {t("chatbot.greeting")}
                </div>

                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`${styles.message} ${
                      message.role === "user" ? styles.user : styles.assistant
                    }`}
                  >
                    {message.content}
                  </div>
                ))}

                {isLoading && (
                  <div className={`${styles.message} ${styles.assistant}`}>
                    <span
                      className={styles.typing}
                      aria-label={t("chatbot.thinking")}
                    >
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </div>
                )}

                {hasError && (
                  <div className={`${styles.message} ${styles.error}`}>
                    {t("chatbot.error")}
                  </div>
                )}

                {suggestions.length > 0 && !isLoading && !hasError && (
                  <div
                    className={styles.replyOptions}
                    aria-label={t("heroChat.suggestionsLabel")}
                  >
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        className={styles.replyOption}
                        onClick={() => sendMessage(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {proposal && !isLoading && (
                  <button
                    type="button"
                    className={styles.proposalJump}
                    onClick={() =>
                      proposalRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                      })
                    }
                  >
                    {t("heroChat.proposal.viewCta")}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 5v14" />
                      <path d="m19 12-7 7-7-7" />
                    </svg>
                  </button>
                )}

                {info.callRecommended && !isLoading && !proposal && (
                  <div className={styles.chatCtaBlock}>
                    <button
                      type="button"
                      className={styles.chatCta}
                      onClick={handleTakeover}
                    >
                      {t("chatbot.chatCta")}
                    </button>
                    <p className={styles.chatCtaNote}>
                      {t("chatbot.chatCtaNote")}
                    </p>
                  </div>
                )}
              </div>
            )}

            <form className={styles.composer} onSubmit={handleSubmit}>
              <textarea
                ref={inputRef}
                className={styles.promptInput}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("heroChat.placeholder")}
                maxLength={2000}
                autoComplete="off"
                aria-label={t("heroChat.placeholder")}
              />
              <button
                type="submit"
                className={styles.send}
                disabled={isLoading || !input.trim()}
                aria-label={t("chatbot.send")}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 2 11 13" />
                  <path d="m22 2-7 20-4-9-9-4Z" />
                </svg>
              </button>
            </form>

            {!chatMode && (
              <div className={styles.chips} aria-label={t("heroChat.chipsLabel")}>
                {chips.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    className={styles.chip}
                    onClick={() => sendMessage(chip.prompt)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}

            <p className={styles.disclaimer}>
              {t("chatbot.disclaimer")}{" "}
              <Link href="/datenschutz" className={styles.disclaimerLink}>
                {t("chatbot.privacyLinkText")}
              </Link>
            </p>
          </div>

          <aside
            ref={proposalRef}
            className={styles.proposal}
            aria-label={t("heroChat.proposal.eyebrow")}
          >
            {proposal && (
              <div className={styles.proposalInner}>
                <div className={styles.proposalHeader}>
                  <p className={styles.proposalEyebrow}>
                    {t("heroChat.proposal.eyebrow")}
                  </p>
                  <h3 className={styles.proposalTitle}>{proposal.title}</h3>
                </div>

                <div className={styles.summaryCard}>
                  <p>{proposal.summary}</p>
                </div>

                <div
                  className={styles.moduleGrid}
                  aria-label={t("heroChat.proposal.modulesLabel")}
                >
                  {proposal.modules.map((module, index) => (
                    <div key={module} className={styles.module}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{module}</strong>
                    </div>
                  ))}
                </div>

                {proposal.questions.length > 0 && (
                  <div className={styles.questionsCard}>
                    <h4>{t("heroChat.proposal.questionsTitle")}</h4>
                    <ul>
                      {proposal.questions.map((question) => (
                        <li key={question}>{question}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div
                  className={styles.timeline}
                  aria-label={t("heroChat.proposal.timelineLabel")}
                >
                  {timelineSteps.map((step) => (
                    <div key={step.period} className={styles.step}>
                      <small>{step.period}</small>
                      <strong>{step.title}</strong>
                    </div>
                  ))}
                </div>

                <div className={styles.proposalActions}>
                  <button
                    type="button"
                    className={styles.proposalPrimary}
                    onClick={handleTakeover}
                    data-umami-event="hero-proposal-call"
                  >
                    {t("chatbot.chatCta")}
                  </button>
                  <button
                    type="button"
                    className={styles.proposalReset}
                    onClick={handleReset}
                  >
                    {t("heroChat.proposal.ctaReset")}
                  </button>
                </div>

                <p className={styles.proposalNote}>{t("chatbot.chatCtaNote")}</p>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
};

export default HeroChat;
